import React from 'react';

// fa-shapes, triangle remplacé par une étoile. FontAwesome n'a pas d'icône
// combinant étoile + carré + cercle, d'où ce SVG : les tracés viennent tels
// quels de FA Pro 6.4.2 (cercle et carré de faShapes duotone, étoile de faStar
// solid), l'étoile étant remise à l'échelle dans l'emplacement du triangle.
const CIRCLE = 'M128 512a128 128 0 1 0 0-256 128 128 0 1 0 0 256z';
const SQUARE =
  'M328 272c-22.1 0-40 17.9-40 40V456c0 22.1 17.9 40 40 40H472c22.1 0 40-17.9 40-40V312c0-22.1-17.9-40-40-40H328z';
const STAR =
  'M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z';

// fa-shapes laisse 32 unités entre ses formes, calibré pour des silhouettes
// pleines. Avec un contour, chaque forme mord 13 unités de plus (moitié de
// l'épaisseur, le contour étant centré sur le tracé) : il ne restait que 6
// unités de blanc et les formes se touchaient presque. On rétrécit le cercle et
// le carré autour de leur propre centre, ce qui creuse l'écart sans déplacer
// personne (écart = 272 - 240 * SHAPE_SCALE - STROKE_WIDTH, soit 30 unités).
const SHAPE_SCALE = 0.9;
const shrinkAround = (cx, cy) => `translate(${cx} ${cy}) scale(${SHAPE_SCALE}) translate(${-cx} ${-cy})`;
const CIRCLE_TRANSFORM = shrinkAround(128, 384);
const SQUARE_TRANSFORM = shrinkAround(400, 384);

// L'étoile, elle, est posée directement à sa taille finale : SHAPE_SCALE
// l'aurait rétrécie une deuxième fois alors qu'elle ne touche personne — son
// écart au cercle vient déjà du fait qu'elle est plus courte que son créneau.
// À 202 unités elle se lisait plus petite que le carré : une étoile à cinq
// branches ne couvre qu'environ 40 % de sa boîte, un carré 100 %, donc à boîte
// égale elle pèse moins. À 224 (la hauteur du triangle qu'elle remplace) elle
// fait 231 de large, soit le diamètre du cercle rétréci.
// STAR_FIT ramène la hauteur du tracé source (514 dans son viewBox 576x512) à
// STAR_HEIGHT ; le décalage -289.7 est le centre horizontal réel du tracé, qu'on
// aligne sur celui du triangle (288).
const STAR_HEIGHT = 224;
const STAR_FIT = STAR_HEIGHT / 514;
const STAR_TRANSFORM = `translate(288 0) scale(${STAR_FIT.toFixed(4)}) translate(-289.7 0)`;

// Intérieur clair, contour plein : les trois formes se lisent comme un logo là
// où l'aplat uniforme donnait trois pavés opaques à 25 px.
// STROKE_WIDTH est en unités du viewBox (512) — ~1,3 px à la taille d'affichage
// de la palette (25 px). Le contour est centré sur le tracé, d'où les 18 unités
// de marge du viewBox : sans elles les formes qui touchent les bords (cercle à
// gauche, carré à droite) verraient leur contour rogné.
const STROKE_WIDTH = 26;
const FILL_OPACITY = 0.25;
// Un transform met aussi le contour à l'échelle : sans ces divisions l'étoile
// sortirait à 26 * 0.436 ≈ 11, deux fois plus fine que le reste.
const SHAPE_STROKE_WIDTH = STROKE_WIDTH / SHAPE_SCALE;
const STAR_STROKE_WIDTH = STROKE_WIDTH / STAR_FIT;

// Même signature d'appel que DuotoneLightIcon (`style` porte fontSize et color)
// pour rester interchangeable avec les autres icônes de la palette.
const ShapesStarIcon = ({ style = {}, className = '' }) => {
  const { color = 'currentColor', ...rest } = style;

  return (
    <svg
      viewBox='-18 -18 548 548'
      width='1em'
      height='1em'
      fill={color}
      fillOpacity={FILL_OPACITY}
      stroke={color}
      strokeWidth={SHAPE_STROKE_WIDTH}
      strokeLinejoin='round'
      className={className}
      style={rest}
      aria-hidden='true'
      focusable='false'
    >
      <path d={STAR} transform={STAR_TRANSFORM} strokeWidth={STAR_STROKE_WIDTH} />
      <path d={CIRCLE} transform={CIRCLE_TRANSFORM} />
      <path d={SQUARE} transform={SQUARE_TRANSFORM} />
    </svg>
  );
};

export default ShapesStarIcon;
